import { useEffect, useState } from "react";
import {
  ArrowLeft,
  FileText,
  Heart,
  PackageCheck,
  ReceiptText,
  ShoppingBag,
  Trash2,
} from "lucide-react";
import { collection, deleteDoc, doc, onSnapshot, query, where } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { useAuth } from "../components/auth/AuthGate";
import { db } from "../firebase/firebase";
import { saveWorkflowSelection } from "../lib/workflowBooking";
import { readSavedDrafts, deleteSavedDraft, hydrateDraftFromSavedRecord } from "../lib/savedDrafts";
import { listenToCustomerTailorQuotes, tailorQuoteStatusLabel } from "../services/tailorQuoteService";

const meta = {
  cart: [ShoppingBag, "My Cart", "Products and services ready for checkout"],
  orders: [PackageCheck, "My Orders", "Track active and completed orders"],
  quotations: [ReceiptText, "My Quotations", "Review and continue tailor quotation requests"],
  wishlist: [Heart, "My Wishlist", "Products you have saved"],
  drafts: [FileText, "My Drafts", "Continue unfinished orders"],
};

const transitionedQuoteStatuses = new Set([
  "customer_accepted",
  "partial_paid",
  "awaiting_final_payment",
  "paid",
  "full_payment_done_at_home",
]);

const imageOf = (item) =>
  item.designImageUrl ||
  item.designImageDataUrl ||
  item.imageUrl ||
  item.image ||
  item.mainImage ||
  item.garment?.image ||
  "";

export default function CustomerCenterPage() {
  const { section } = useParams();
  const navigate = useNavigate();
  const { user, loading, openAuth } = useAuth();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [drafts, setDrafts] = useState(() => readSavedDrafts());

  useEffect(() => {
    if (section === "drafts") setDrafts(readSavedDrafts());
  }, [section]);

  useEffect(() => {
    setItems([]);
    setError("");
    if (!user || !section) return undefined;

    if (section === "wishlist") {
      return onSnapshot(
        collection(db, "customers", user.uid, "wishlist"),
        (snapshot) => setItems(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }))),
        () => setError("Unable to load your wishlist."),
      );
    }

    if (section === "orders") {
      return onSnapshot(
        query(collection(db, "orders"), where("customerId", "==", user.uid)),
        (snapshot) => {
          setItems(
            snapshot.docs
              .map((entry) => ({ id: entry.id, ...entry.data(), recordType: "order" }))
              .sort((first, second) => (second.createdAt?.seconds || 0) - (first.createdAt?.seconds || 0)),
          );
        },
        () => setError("Unable to load your orders."),
      );
    }

    if (section === "quotations") {
      return listenToCustomerTailorQuotes(
        user.uid,
        (quotes) => setItems(quotes.filter((quote) => !transitionedQuoteStatuses.has(quote.status))),
        () => setError("Unable to load tailor quotations."),
      );
    }

    return undefined;
  }, [user, section]);

  if (loading) return <div className="min-h-screen bg-[#FFF8F2]" />;
  if (!user) {
    return (
      <div className="min-h-screen bg-[#FFF8F2]">
        <Navbar />
        <main className="pt-32 text-center">
          <h1 className="font-display text-4xl">My StitchB</h1>
          <p className="mt-3">Sign in to access your private customer area.</p>
          <button data-public-action="true" onClick={openAuth} className="btn-primary mt-6">
            Login / Sign up
          </button>
        </main>
      </div>
    );
  }

  const [Icon, title, subtitle] = meta[section] || meta.cart;
  const visible = section === "drafts" ? drafts : items;

  const openWishlist = (item) => {
    saveWorkflowSelection(item.workflow, { ...item, id: item.itemId || item.id });
    const group = item.categoryId || item.sectionId || "all";
    navigate(
      item.workflow === "fabric-store"
        ? `/services/fabric-store/${group}/${item.itemId}/details`
        : `/services/${item.workflow}/${group}/${item.itemId}/details`,
    );
  };

  const openRecord = (item) =>
    navigate(item.recordType === "tailor_quote" ? `/account/quotes/${item.id}` : `/account/orders/${item.id}`);

  const openDraft = (item) => {
    const draft = hydrateDraftFromSavedRecord(item.kind, item);
    if (!draft) return;
    navigate(item.kind === "alteration" ? "/services/alterations" : item.route || "/services");
  };

  const removeDraft = (event, item) => {
    event.stopPropagation();
    deleteSavedDraft(item.kind, item.id);
    setDrafts(readSavedDrafts());
  };

  return (
    <div className="min-h-screen bg-[#FFF8F2]">
      <Navbar />
      <main className="pt-28 pb-20">
        <section className="max-w-[1000px] mx-auto px-6">
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm font-semibold text-[#F46524]">
            <ArrowLeft size={16} /> Back home
          </button>

          <div className="mt-8 flex items-center gap-4">
            <span className="w-14 h-14 bg-[#FFEDDD] text-[#F46524] flex items-center justify-center">
              <Icon />
            </span>
            <div>
              <h1 className="font-display text-4xl">{title}</h1>
              <p className="text-[#6b625c] mt-1">{subtitle}</p>
            </div>
          </div>

          {(section === "orders" || section === "quotations") ? (
            <div className="mt-7 inline-flex border border-[#E5D4C8] bg-white p-1" aria-label="Orders and quotations">
              <button
                type="button"
                onClick={() => navigate("/account/orders")}
                className={`px-5 py-2.5 font-semibold ${section === "orders" ? "bg-[#6B1F2B] text-white" : "text-[#5b5a56]"}`}
              >
                Orders
              </button>
              <button
                type="button"
                onClick={() => navigate("/account/quotations")}
                className={`px-5 py-2.5 font-semibold ${section === "quotations" ? "bg-[#6B1F2B] text-white" : "text-[#5b5a56]"}`}
              >
                Quotations
              </button>
            </div>
          ) : null}

          {error ? <div className="card-soft p-6 mt-8 text-red-600">{error}</div> : null}

          {section === "cart" ? (
            <div className="card-soft p-10 mt-8 text-center">
              <ShoppingBag size={42} className="mx-auto text-[#F46524]" />
              <h2 className="font-display text-2xl mt-4">Your cart is empty</h2>
              <button onClick={() => navigate("/services")} className="btn-primary mt-6">Explore services</button>
            </div>
          ) : !visible.length ? (
            <div className="card-soft p-10 mt-8 text-center">
              <Icon size={42} className="mx-auto text-[#F4B28E]" />
              <h2 className="font-display text-2xl mt-4">
                {section === "quotations" ? "No quotations yet" : "Nothing here yet"}
              </h2>
              <p className="mt-2 text-[#6b625c]">
                {section === "quotations"
                  ? "Your tailor quotation requests will appear here."
                  : `Your ${section} will appear here.`}
              </p>
              <button onClick={() => navigate("/services")} className="btn-primary mt-6">Explore services</button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5 mt-8">
              {visible.map((item) => {
                if (section === "drafts") {
                  return (
                    <article key={item.id} className="card-soft p-5 flex gap-4 cursor-pointer" onClick={() => openDraft(item)}>
                      <span className="w-16 h-16 shrink-0 bg-[#FFEDDD] text-[#F46524] flex items-center justify-center"><FileText size={22} /></span>
                      <div className="flex-1 min-w-0">
                        <div className="font-label text-[#F46524]">Saved draft</div>
                        <h3 className="font-display text-2xl mt-2 truncate">{item.title}</h3>
                        <button onClick={(event) => removeDraft(event, item)} className="mt-3 text-red-600 inline-flex gap-2 items-center"><Trash2 size={15} /> Remove</button>
                      </div>
                    </article>
                  );
                }
                if (section === "wishlist") {
                  return (
                    <article key={item.id} onClick={() => openWishlist(item)} className="card-soft p-5 cursor-pointer">
                      <h3 className="font-display text-2xl">{item.name || item.title}</h3>
                      <p className="text-sm text-[#5b5a56] mt-2">Open saved item</p>
                      <button onClick={(event) => { event.stopPropagation(); deleteDoc(doc(db, "customers", user.uid, "wishlist", item.id)); }} className="mt-4 text-red-600 inline-flex gap-2"><Trash2 size={15} /> Remove</button>
                    </article>
                  );
                }

                const quote = item.recordType === "tailor_quote";
                return (
                  <article key={`${item.recordType}-${item.id}`} onClick={() => openRecord(item)} className="card-soft overflow-hidden cursor-pointer">
                    <div className="h-48 bg-[#FFF3E8]">
                      {imageOf(item) ? <img src={imageOf(item)} alt="" className="w-full h-full object-contain" /> : <span className="h-full flex items-center justify-center text-[#F46524]"><PackageCheck size={40} /></span>}
                    </div>
                    <div className="p-5">
                      <div className="font-label text-[#F46524]">{quote ? "Tailor quotation" : "Order"}</div>
                      <h3 className="font-display text-2xl mt-2">{item.garmentName || item.garment?.name || item.orderType || "StichB service"}</h3>
                      <p className="text-sm text-[#5b5a56] mt-2 capitalize">{quote ? tailorQuoteStatusLabel(item.status) : String(item.status || "Order placed").replaceAll("_", " ")}</p>
                      {quote && item.finalQuoteAmount ? <p className="font-semibold mt-3">Quote: ₹{Number(item.finalQuoteAmount).toLocaleString("en-IN")}</p> : null}
                      {quote ? <p className="text-sm font-semibold text-[#F46524] mt-4">View and continue</p> : null}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
